import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

// The valid values the server will accept. Typing these by hand is how order
// 7511 ended up stored as "off white" and sat unshippable, so they are picked
// from a list here rather than typed.
const COLORS = ['Black', 'Off White', 'Blue', 'Maroon', 'Beige', 'Pink'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const field = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 14,
  background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155'
};
const label = { display: 'block', fontSize: 12, color: '#94a3b8', margin: '12px 0 4px' };

// A custom line either stays a one-off print or points at a design we already
// stock. The second case matters: it makes the item draw that design's print
// from inventory instead of being treated as a blank-only custom job.
export default function CustomItemModal({ order, item, onClose, onSaved }) {
  const startsCustom = !item.designCode || item.designCode === 'CUSTOM';

  const [mode, setMode] = useState(startsCustom ? 'custom' : 'inventory');
  const [designs, setDesigns] = useState([]);
  const [query, setQuery] = useState(startsCustom ? '' : item.designName || '');
  const [picked, setPicked] = useState(startsCustom ? null : { designCode: item.designCode, designName: item.designName });
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(COLORS.includes(item.color) ? item.color : '');
  const [size, setSize] = useState(SIZES.includes(item.size) ? item.size : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/prints`)
      .then(r => setDesigns(r.data || []))
      .catch(() => setError('Could not load the design list'));
  }, []);

  // close the suggestion list when clicking elsewhere
  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...designs].sort((a, b) => (a.designName || '').localeCompare(b.designName || ''));
    if (!q) return list.slice(0, 50);
    return list.filter(d =>
      (d.designName || '').toLowerCase().includes(q) ||
      (d.designCode || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [designs, query]);

  const save = async () => {
    setError(null);
    if (mode === 'inventory' && !picked) return setError('Pick a design from the list, or switch to Custom design.');
    if (!color) return setError('Choose a blank colour.');
    if (!size) return setError('Choose a size.');

    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/manifest/set-item-blank/${order._id}`, {
        itemId: item._id,
        color,
        size,
        designCode: mode === 'custom' ? 'CUSTOM' : picked.designCode
      });
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0f172a', border: '1px solid #334155', borderRadius: 10,
          padding: 20, width: 460, maxWidth: '92vw', color: '#e2e8f0'
        }}
      >
        <div style={{ fontSize: 16, marginBottom: 2 }}>Set up this item</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          Order {order.shopifyOrderNumber} · {item.designName}
        </div>

        <label style={label}>What is being printed?</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['custom', 'Custom design'], ['inventory', 'A design we stock']].map(([val, text]) => (
            <button
              key={val}
              onClick={() => setMode(val)}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                background: mode === val ? '#1d4ed8' : '#1e293b',
                color: mode === val ? '#fff' : '#94a3b8',
                border: `1px solid ${mode === val ? '#1d4ed8' : '#334155'}`
              }}
            >
              {text}
            </button>
          ))}
        </div>

        {mode === 'inventory' && (
          <div ref={boxRef} style={{ position: 'relative' }}>
            <label style={label}>Which design? (start typing to search)</label>
            <input
              style={field}
              value={query}
              placeholder="e.g. Selective Hearing, or SLH001"
              onChange={(e) => { setQuery(e.target.value); setPicked(null); setOpen(true); }}
              onFocus={() => setOpen(true)}
            />
            {picked && (
              <div style={{ fontSize: 12, color: '#86efac', marginTop: 4 }}>
                ✓ {picked.designName} ({picked.designCode})
              </div>
            )}
            {open && matches.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                maxHeight: 220, overflowY: 'auto', background: '#1e293b',
                border: '1px solid #334155', borderRadius: 6, marginTop: 2
              }}>
                {matches.map(d => (
                  <div
                    key={d.designCode}
                    onClick={() => {
                      setPicked({ designCode: d.designCode, designName: d.designName });
                      setQuery(d.designName);
                      setOpen(false);
                    }}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #0f172a' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#334155')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {d.designName}
                    <span style={{ color: '#64748b', marginLeft: 6, fontFamily: 'monospace', fontSize: 11 }}>
                      {d.designCode}
                    </span>
                    <span style={{ color: d.quantity > 0 ? '#64748b' : '#f87171', marginLeft: 6, fontSize: 11 }}>
                      · {d.quantity} in stock
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <label style={label}>Blank colour</label>
        <select style={field} value={color} onChange={(e) => setColor(e.target.value)}>
          <option value="">Choose a colour…</option>
          {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label style={label}>Size</label>
        <select style={field} value={size} onChange={(e) => setSize(e.target.value)}>
          <option value="">Choose a size…</option>
          {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#fca5a5' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 6, background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            style={{ padding: '8px 14px', borderRadius: 6, background: '#1d4ed8', color: '#fff', border: 'none', cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
